import React, { useRef, useState, useMemo } from 'react';
import { Column, Table, RowData } from '@tanstack/react-table';
import { ReactComponent as FilterIcon } from '../../../images/filter.svg';
import { ReactComponent as ChevronDown } from '../../../images/chevron-down.svg';
import { FilterModal } from '../../filter-modal/FilterModal';

// You will need to define your row data type, e.g., extends RowData
// For this example, we will use a generic TData.
interface DefaultColumnFilterProps<TData extends RowData> {
  column: Column<TData, unknown>;
  table: Table<TData>;
  appliedFilters: { columnId: string; filterValue: any }[];
  onFilterChange: (columnId: string, value: unknown) => void;
}

/**
 * Reusable column filter component for data tables
 * Provides a unified filtering interface with filter icon and dropdown modal
 */
export const DefaultColumnFilterTanstackV8 = <TData extends RowData>({
  column,
  table,
  appliedFilters: globalAppliedFilters,
  onFilterChange,
}: DefaultColumnFilterProps<TData>) => {
  const [showFilter, setShowFilter] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);

  // New v8 API: getFilterValue()
  const filterValue = column.getFilterValue();
  const id = column.id;

  // Find the applied filter for THIS column only
  const currentColumnFilter = globalAppliedFilters?.find((filter) => filter.columnId === id);
  const hasFilterApplied = !!currentColumnFilter;

  // Helper to generate unique filter options, similar to old preFilteredRows logic
  const uniqueColumnValues = useMemo(() => {
    // In TanStack Table v8, we get all rows and map the cell values.
    // Use the getCoreRowModel to ensure you are getting the unfiltered data source.
    const allRows = table.getCoreRowModel().flatRows;
    const values = new Set<string>();

    allRows.forEach((row) => {
      // Use column.accessorFn or row.getValue(id) to safely get the value
      const cellValue = row.getValue<string>(id);
      if (cellValue !== undefined && cellValue !== null) {
        values.add(String(cellValue));
      }
    });

    return Array.from(values).sort();
  }, [id, table]);

  const handleFilterToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowFilter((prev) => !prev);
  };

  const handleSetFilter = (value: unknown) => {
    // 1. Update the TanStack Table state using the column's new setter function
    column.setFilterValue(value);
    onFilterChange(id, value);

    // Close modal after filter is applied
    setTimeout(() => setShowFilter(false), 0);
  };

  const handleClearFilter = () => {
    // 1. Clear the filter in TanStack Table
    column.setFilterValue(undefined);

    // 2. Clear the filter in the parent state (by passing undefined)
    onFilterChange(id, undefined);

    setShowFilter(false);
  };

  const handleClose = () => setShowFilter(false);

  return (
    <div
      ref={triggerRef}
      onClick={(e) => {
        // Prevent click from propagating to the row/header, but allow interaction
        e.stopPropagation();
      }}
    >
      {/* Icon check based on the new 'hasFilterApplied' flag */}
      {hasFilterApplied ? <FilterIcon onClick={handleFilterToggle} /> : <ChevronDown onClick={handleFilterToggle} />}

      {showFilter && (
        <FilterModal
          columnId={id}
          setFilter={handleSetFilter}
          // Pass the generated unique values instead of the entire preFilteredRows
          // You'll need to update FilterModal to accept this array.
          preFilteredRows={uniqueColumnValues}
          filterValue={filterValue}
          // Pass the simpler clear function
          clearFilterForColumn={handleClearFilter}
          onClose={handleClose}
          triggerRef={triggerRef}
          // The appliedFilters should now come from the parent's global state
          appliedFilters={globalAppliedFilters}
        />
      )}
    </div>
  );
};
